/**
 * GlobalView — Header Component
 *
 * Top bar (§11.2): Logo, search placeholder, mode indicator,
 * system time, user avatar.
 */

import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import ViewModeSwitch from '../ui/ViewModeSwitch';
import styles from './Header.module.css';

export default function Header({ onShowAdmin }: { onShowAdmin?: () => void }) {
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const signOut = useAuthStore((s) => s.signOut);
  const [time, setTime] = useState(formatTime());
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setTime(formatTime()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className={styles.header} id="app-header">
      {/* HUD bracket corner (right) */}
      <span className={styles.bracketRight} />

      {/* Logo */}
      <div className={styles.logo}>
        <span className={styles.logoIcon}>🌍</span>
        <span className={styles.logoText}>GLOBALVIEW</span>
      </div>

      {/* Search placeholder */}
      <div className={styles.searchBar} id="global-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span className={styles.searchPlaceholder}>Suche: Orte, Koordinaten, Objekte…</span>
      </div>

      {/* Right section */}
      <div className={styles.rightSection}>
        {/* Visual Mode Preset Switcher (§14.2) */}
        <ViewModeSwitch />

        {/* Mode indicator */}
        <div className={styles.modeIndicator}>
          <span className={styles.liveDot} />
          <span className={styles.modeText}>LIVE</span>
        </div>

        {/* System time */}
        <div className={styles.clock} id="system-clock">
          {time}
        </div>

        {/* User */}
        <div className={styles.userArea}>
          <button
            className={styles.avatarButton}
            onClick={() => setShowUserMenu((v) => !v)}
            id="user-menu-button"
          >
            {user?.photoUrl ? (
              <img
                src={user.photoUrl}
                alt={user.displayName || 'User'}
                className={styles.avatarImg}
              />
            ) : (
              <div className={styles.avatarFallback}>
                {user?.displayName?.[0]?.toUpperCase() || '?'}
              </div>
            )}
          </button>

          {showUserMenu && (
            <div className={styles.userMenu} id="user-dropdown">
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user?.displayName || 'User'}</span>
                <span className={styles.userEmail}>{user?.email}</span>
                <span className={styles.userRole}>{role?.toUpperCase()}</span>
              </div>
              <div className={styles.menuDivider} />
              {onShowAdmin && (
                <button
                  className={styles.menuItem}
                  onClick={() => { onShowAdmin(); setShowUserMenu(false); }}
                  id="admin-link"
                >
                  Admin-Panel
                </button>
              )}
              <button
                className={styles.menuItem}
                onClick={signOut}
                id="sign-out-button"
              >
                Abmelden
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function formatTime(): string {
  return new Date().toLocaleTimeString('de-CH', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}
